import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { z } from 'zod';
import ProgressButton from '@/components/common/progress-button';
import { transformSpecialScripts } from '@/components/conversations/conversation-list/message-list-item/message-content/transform-scripts';
import { FieldMessage } from '@/components/fields/field-message';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useUpdateGroupInfo from '@/hooks/groups/use-update-group-info';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import ROUTES from '@/routes';
import { useConversationSessionStore } from '@/stores/conversation-session';
import cn from '@/utils/merge-class-names';
import { generateSessionKey } from '@/utils/session';

export default function GroupAnnouncementRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: groupId } = useTypedParams(ROUTES.MESSAGES.GROUPS.SETTINGS.ANNOUNCEMENT);

  const { data: groupInfo, isLoading, canManage } = useGroupInfo(groupId);
  const [editable, setEditable] = useState(!groupInfo?.GroupData?.Info);
  const { mutateAsync: updateGroupInfo, isPending: isUpdatingGroupInfo } = useUpdateGroupInfo(groupId);
  const {
    register,
    handleSubmit,
    resetField,
    formState: { errors, isDirty },
    watch,
  } = useForm<{ info: string }>({
    mode: 'onChange',
    resolver: zodResolver(announcementFormScheme),
  });
  const info = watch('info');

  const content = useMemo(
    () => (groupInfo?.GroupData?.Info ? transformSpecialScripts(0, groupInfo.GroupData.Info) : ''),
    [groupInfo?.GroupData?.Info],
  );

  useEffect(() => {
    if (!groupInfo?.GroupData?.Info) return;

    if (canManage) {
      resetField('info', { defaultValue: groupInfo.GroupData.Info, keepDirty: false });
    }

    useConversationSessionStore
      .getState()
      .setLastReadNotice(generateSessionKey('group', groupId), groupInfo.GroupData.Info);
  }, [canManage, groupId, groupInfo?.GroupData?.Info]);

  useAdjustLayout();

  const submit = async (data: { info: string }) => {
    if (isDirty) {
      const result = await updateGroupInfo(data.info);

      if (result.Code === 0) {
        navigate(-1);
      }
    }
    setEditable(false);
  };

  return (
    <PageLayout
      title={t('conversation.setting.group.announcement')}
      headerConfig={{
        navigateBack: true,
      }}
      className='mb-safe-or-6'>
      <LoadingLayout promise={isLoading}>
        {() => (
          <div className='mx-5 flex flex-1 flex-col overflow-y-auto desktop:mx-0'>
            {canManage ? (
              <form
                onSubmit={handleSubmit(submit)}
                className='flex flex-1 flex-col justify-between gap-2'>
                <label
                  htmlFor='info'
                  className={cn('form-control w-full', { hidden: !editable })}>
                  <textarea
                    {...register('info')}
                    id='info'
                    rows={6}
                    placeholder={t('conversation.setting.group.content-placeholder')}
                    className={cn(
                      'textarea textarea-bordered textarea-lg bg-neutral-0 px-4 py-2 text-body1 focus:border-primary focus:outline-none',
                      { 'textarea-error': errors.info },
                    )}
                  />

                  <FieldMessage
                    name='info'
                    errors={errors}
                  />

                  <span className='text-right text-sm text-secondary'>{info?.length ?? 0}/ 500</span>
                </label>
                {!editable && (
                  <p className='break-words px-4 py-2 text-body1'>{content || t('conversation.no-group-notice')}</p>
                )}
                {editable ? (
                  <ProgressButton
                    as='submit'
                    disabled={!isDirty}
                    isLoading={isUpdatingGroupInfo}
                    className='btn btn-primary'
                    label='common.confirm'
                  />
                ) : (
                  <button
                    type='button'
                    className='btn btn-primary'
                    onClick={(e) => {
                      e.preventDefault();
                      setEditable(true);
                    }}>
                    {t('common.edit')}
                  </button>
                )}
              </form>
            ) : (
              <div className='px-4 py-2'>{content || t('conversation.no-group-notice')}</div>
            )}
          </div>
        )}
      </LoadingLayout>
    </PageLayout>
  );
}

const announcementFormScheme = z.object({ info: z.string().max(500, 'errors.string.max-length|count=500') });
